import { ApiRequestError, type EndpointResponse, type guest } from '@croissants/contracts';
import { isServiceDay, serviceDaysBetween } from '@croissants/contracts/projections';

/**
 * The multi-select day strip's state rules (PRD GST-002, GST-010 - multi-day
 * plan decisions D1, D2, D11).
 *
 * ONE picker for every order: the strip's chips are the mornings of the
 * 14-day horizon (D7), every chip toggleable, and a one-day order is simply
 * one chip selected - there is no separate code path. The facts these
 * functions answer are DERIVED from the picker's own windows response, never
 * from a second predicate.
 *
 * THE DAYS DECIDE THE TIMES, and never the other way round (founder decision,
 * 2026-08-24). Every chip is a plain toggle: pressing one selects it, pressing
 * it again unselects it, and no press ever moves a chip the guest did not
 * touch - not to select one for them, not to drop one they had. The selection
 * may therefore be empty, and that is a state the screen renders rather than
 * a state it corrects. The half-hours below are then whatever every selected
 * morning can serve (`sharedWindows`), so a time on screen fits the whole
 * stay by construction and a time that stops fitting simply leaves the list -
 * silently, with nothing to confirm and nothing to dismiss.
 *
 * That replaces the earlier rule in which a chosen TIME greyed and deselected
 * days (D2) and a tap on a grey chip opened a dialog to explain or offer it.
 * Both are gone: no chip is ever grey because of a time, and the picker asks
 * the guest nothing.
 *
 * What survives from D1 is the day-level rule - a morning is selectable iff
 * it has at least one selectable window - and the
 * requirement that a morning nothing can serve is SHOWN, disabled, rather
 * than quietly missing (GST-002). The one exception is a today that has run
 * out of day: it is dropped from the strip outright (`dayPassed`), because
 * "Passed" is a state, not an offer, and the strip is a list of offers.
 *
 * Pure, and apart from the screen, because calendar logic is where the quiet
 * bugs live (the DST walk in `stripDays` runs at UTC midday so a date is
 * never skipped or doubled by a daylight-saving change).
 */

type WindowsResponse = EndpointResponse<typeof guest.listWindows>;
export type OfferedDay = WindowsResponse['serviceDays'][number];

/** What the platform sells, as the picker reads it (PRD OPS-025, GST-042). */
export interface OrderingModes {
  readonly preorder: boolean;
  readonly immediate: boolean;
}

/**
 * Today's offer, if the answer carries one (PRD RTE-034).
 *
 * Asked by KIND rather than by comparing dates, because "which morning is
 * today" is the server's reading of the platform clock and not the browser's -
 * the same reason the strip labels its cards from `serverTime` (TST-003).
 */
export function todayOffer(days: readonly OfferedDay[]): OfferedDay | undefined {
  return days.find((day) => day.kind === 'TODAY');
}

/**
 * The mornings this guest may be offered, given what the platform sells
 * (GST-042).
 *
 * The horizon is the server's - it starts at today when immediate is on and at
 * tomorrow when it is not - and this narrows it by the one rule a screen can
 * apply on its own: with pre-order off, today is the ONLY morning that can be
 * ordered, so the picker reached from "Change" is scoped to it. Stated here
 * rather than trusted to the answer, so a stale response cached from before the
 * switch flipped cannot put a morning on the strip that checkout would refuse.
 */
export function daysForModes(
  days: readonly OfferedDay[],
  modes: OrderingModes | null,
): readonly OfferedDay[] {
  if (modes === null || modes.preorder) return days;
  return days.filter((day) => day.kind === 'TODAY');
}

/**
 * The window pre-selected when Today is the anchor (PRD RTE-034).
 *
 * The server tags exactly one window `soonest` - the ASAP option, the first
 * that its accept, prepare, travel and margin arithmetic can still reach - and
 * the picker opens on it, because that is what a guest ordering for now wants.
 * Null on a pre-order morning, where every window is equally far away and
 * nothing should be chosen for the guest.
 */
export function soonestLocalStart(day: OfferedDay | undefined): string | null {
  if (day === undefined || day.kind !== 'TODAY') return null;
  const tagged = day.windows.find((offer) => offer.soonest && offer.selectable);
  return (tagged ?? day.windows.find((offer) => offer.selectable))?.window.localStart ?? null;
}

/** The three facts a draft carries about the morning it was settled on. */
export interface SoonestMorning {
  readonly serviceDay: string;
  readonly windowStart: string;
  /** RTE-034: today's ASAP ROW was taken, not a time the guest named on it. */
  readonly soonest: boolean;
  readonly selectedDays: readonly string[];
}

/**
 * TODAY, AS SOON AS WE CAN - the whole of it, derived once (PRD RTE-034,
 * GST-042).
 *
 * Two callers settle a draft on this and they must settle it identically: the
 * immediate-only journey, which has no picker at all and writes it behind the
 * shelf (`hooks/useImmediateWindow`), and mode B2's "Order now" button, which
 * is that same journey offered as a choice (founder, 2026-09-20). Written
 * twice it is two readings of which row is the soonest one, and the second
 * reading is the one that quietly takes a named 16:00 slot for an ASAP order.
 *
 * `soonest` is the SERVER's tag and never a position in the list; the fallback
 * is the first SELECTABLE window, never a greyed one. Null when the answer
 * carries no orderable today, which is the state in which neither caller may
 * write anything.
 */
export function soonestMorning(days: readonly OfferedDay[] | undefined): SoonestMorning | null {
  if (days === undefined) return null;
  const today = todayOffer(days);
  const localStart = soonestLocalStart(today);
  if (today === undefined || localStart === null) return null;
  // The ASAP row itself, by its own tag - never the row that happens to share
  // its start, which on the fallback travel allowance is a real named slot.
  const offer =
    today.windows.find((entry) => entry.soonest && entry.selectable) ??
    today.windows.find((entry) => entry.window.localStart === localStart);
  if (offer === undefined) return null;
  return {
    serviceDay: today.serviceDay,
    windowStart: offer.window.start,
    soonest: offer.soonest === true,
    selectedDays: [today.serviceDay],
  };
}

/**
 * Did the guest choose today's ASAP row, or a time they named on it
 * (PRD RTE-034)?
 *
 * Today offers both, so "is this an order for today" no longer answers "does
 * this have a time to show". The ASAP row is the one the SERVER tagged; a slot
 * the guest picked for four this afternoon is an ordinary chosen window and
 * reads like one on every screen after the picker.
 *
 * False for anything this answer does not recognise - a pre-order morning, a
 * stale start, a horizon that has not loaded - because the fallback that is
 * safe to show is a time the guest picked, never the word "Soonest" over a
 * slot they named.
 */
export function isSoonestChoice(
  offered: WindowsResponse | undefined,
  serviceDay: string | null,
  windowStart: string | null,
): boolean {
  if (serviceDay === null || windowStart === null) return false;
  const day = offered?.serviceDays.find((entry) => entry.serviceDay === serviceDay);
  if (day === undefined || day.kind !== 'TODAY') return false;
  return day.windows.some((offer) => offer.soonest && offer.window.start === windowStart);
}

/**
 * Why this morning cannot simply be tapped, if it cannot (PRD GST-002,
 * RTE-034). Null means the tap selects it and nothing needs saying.
 *
 * Four answers, and they are facts about the MORNING alone - never about the
 * time the guest is holding, which no longer greys anything (founder
 * decision, 2026-08-24):
 *
 *   NOT_SERVED    no bakery serves this address that morning - the server
 *                 offered no day at all, so there is nothing to explain but
 *                 the fact;
 *   DAY_OVER      TODAY, with its list run out: it holds only the part of the
 *                 day still ahead of accept, prepare and travel, and once that
 *                 is empty today has gone by;
 *   FULL          every hour that could reach this morning is taken (RTE-008).
 *
 * The fifth reason this used to carry, OTHER_WINDOW - the morning is fine and
 * the carried TIME is not - cannot arise any more: the times are derived from
 * the selected days rather than the days from the time, so a morning is never
 * refused for a half-hour it cannot serve. It simply narrows the list.
 *
 * Ordered, and the order is the reading order: a morning nothing serves is not
 * "full", and a closed one is not "over".
 */
export type DayBlockReason = 'NOT_SERVED' | 'DAY_OVER' | 'FULL';

export function dayBlock(day: OfferedDay | undefined): DayBlockReason | null {
  if (day === undefined) return 'NOT_SERVED';
  if (day.windows.length === 0) return day.kind === 'TODAY' ? 'DAY_OVER' : 'NOT_SERVED';
  return daySelectable(day) ? null : 'FULL';
}

/**
 * Today, with nothing left of it (founder decision, 2026-08-24): dropped from
 * the strip altogether rather than shown greyed and labelled "Passed".
 *
 * The strip is a list of mornings that can be ordered; a morning that has gone
 * by is not one of them, and a card saying so is a row of the calendar spent
 * telling the guest about the past. Every OTHER unavailable morning stays on
 * the strip, greyed (GST-002) - a closed house and a full one are things the
 * guest can act on tomorrow, or by ordering earlier.
 */
export function dayPassed(day: OfferedDay | undefined): boolean {
  return dayBlock(day) === 'DAY_OVER';
}

/*
 * There is no word left to print on the card itself.
 *
 * `dayNoteKey` returned exactly one - a cutoff that had gone - and the cutoff
 * is gone with it (2026-08-24). Of the three reasons that remain, "full" is
 * already said by the windows themselves, greyed with their own reason (F-5);
 * "not served" is what the card's own grey means; and a morning that has run
 * out of day is not on the strip at all (`dayPassed`).
 */

/** One chip of the strip: the date, and the server's answer for it if any. */
export interface StripDay {
  readonly serviceDay: string;
  /** Absent when nothing can serve the address that morning - the chip greys. */
  readonly day: OfferedDay | undefined;
}

/**
 * The strip's chips: every calendar date from the first offered morning to the
 * last, in order, interior gaps included - a morning nothing serves is a GREY
 * chip, never a silently missing one. Walked in UTC at midday so a DST change
 * neither skips nor doubles a date.
 */
export function stripDays(days: readonly OfferedDay[]): readonly StripDay[] {
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return [];
  // The two ends are checked before they are walked, as the loop this replaced
  // checked them: the answer is validated on the way in, but the guest portal
  // logs rather than refuses a bad shape in production, and an empty strip is a
  // better failure than a thrown render.
  if (!isServiceDay(first.serviceDay) || !isServiceDay(last.serviceDay)) return [];
  const byDay = new Map(days.map((day) => [day.serviceDay, day]));
  // Bounded by the contract's 60-morning cap, never by trust in the input.
  return serviceDaysBetween(first.serviceDay, last.serviceDay, 60).map((serviceDay) => ({
    serviceDay,
    day: byDay.get(serviceDay),
  }));
}

/** D1's day-level rule: at least one selectable window. */
export function daySelectable(day: OfferedDay | undefined): boolean {
  if (day === undefined) return false;
  return day.windows.some((offer) => offer.selectable);
}

/**
 * The selection as the strip, the title and the confirm action all read it:
 * the days the guest has pressed, in ascending order, minus any the answer
 * has since stopped offering.
 *
 * That last filter is the only thing that ever removes a day the guest did not
 * press, and it is not a rule about the guest's choices - it is the list
 * refetching (RTE-034) and a morning going full or past its cutoff while the
 * screen was open. The TIME never removes one: pressing a morning that cannot
 * serve the chosen half-hour narrows the half-hours instead (`sharedWindows`).
 *
 * The result may be empty. A guest who presses their one selected morning
 * again has nothing selected, and the screen says so rather than choosing a
 * morning on their behalf.
 */
export function effectiveSelectedDays(
  days: readonly OfferedDay[],
  tapped: readonly string[],
): readonly string[] {
  const byDay = new Map(days.map((day) => [day.serviceDay, day]));
  return [...tapped].sort().filter((serviceDay) => daySelectable(byDay.get(serviceDay)));
}

/** One half-hour offered on every selected morning. */
export interface SharedWindow {
  /**
   * The offer as the FIRST selected morning states it - the anchor, whose
   * instant is what checkout is given. The later mornings carry the same local
   * wall-clock start (`windowOnMorning`, server-side), never this instant.
   */
  readonly offer: OfferedDay['windows'][number];
  /** Offered, and offered on every selected morning. */
  readonly selectable: boolean;
}

/**
 * The half-hours the guest may choose from, given the mornings they picked
 * (founder decision, 2026-08-24).
 *
 * One window is carried across the whole stay (D11), so the list is the
 * INTERSECTION: a start is offered here only when every selected morning can
 * serve it. That is what makes a day a plain toggle - pressing one more
 * morning can only ever shorten this list, and a time that stops fitting
 * leaves it without a dialog, a warning or a day being deselected behind the
 * guest's back.
 *
 * With exactly ONE morning selected the list is that morning's own, unavailable
 * rows included so each can say why it cannot be taken (F-5: "Complet") - the
 * requirement that a window is never silently missing. With SEVERAL, a row that
 * one of them cannot serve is dropped rather than greyed: "Complet" on a list
 * drawn for four mornings names no morning, and the guest's remedy is to press
 * a morning off, not to read a reason.
 */
export function sharedWindows(
  days: readonly OfferedDay[],
  selectedDays: readonly string[],
): readonly SharedWindow[] {
  const byDay = new Map(days.map((day) => [day.serviceDay, day]));
  const chosen = [...selectedDays]
    .sort()
    .map((serviceDay) => byDay.get(serviceDay))
    .filter((day): day is OfferedDay => day !== undefined);
  const anchor = chosen[0];
  if (anchor === undefined) return [];
  const rows: SharedWindow[] = [];
  for (const offer of anchor.windows) {
    const localStart = offer.window.localStart;
    const everywhere = chosen.every((day) =>
      day.windows.some((other) => other.selectable && other.window.localStart === localStart),
    );
    if (chosen.length > 1 && !everywhere) continue;
    rows.push({ offer, selectable: everywhere });
  }
  return rows;
}

/**
 * The wire's `repeat` from the selected set (GST-010): the FURTHER mornings
 * beside the anchor, with the last selected day as the stay end. One selected
 * day is a one-day order - `null`, no schedule, exactly as before D11.
 */
/**
 * The mornings the wire's repeat may carry: the ones the review PRICED
 * (founder, 2026-09-21).
 *
 * A morning the quote reported unservable is shown on the review - "Not
 * available", by name - and is not part of the order: its total is not in the
 * figure the button charges, so sending it would ask the server to place an
 * order the guest was never offered. Checkout refuses any morning of the set it
 * is given that it cannot serve (`stay_morning_unavailable`), which is exactly
 * what should happen to one that went while the guest was reading the review -
 * and would be wrong for one the review had already ruled out.
 *
 * The morning the quote itself was asked about is never in here: the quote
 * refuses outright when THAT one cannot be served, and the screen says so.
 */
export function servableDays(
  selectedDays: readonly string[],
  mornings: readonly { readonly serviceDay: string; readonly total: number | null }[],
): readonly string[] {
  const unservable = new Set(
    mornings.filter((morning) => morning.total === null).map((morning) => morning.serviceDay),
  );
  return selectedDays.filter((day) => !unservable.has(day));
}

/**
 * GST-010: the order was refused because a morning of the stay is no longer
 * servable. The screen re-prices and the guest reads the order again.
 */
export function isStayMorningGone(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'stay_morning_unavailable';
}

export function stayForWire(
  selectedDays: readonly string[],
): { serviceDays: string[]; stayEndDate: string } | null {
  const ordered = [...selectedDays].sort();
  const anchor = ordered[0];
  const last = ordered[ordered.length - 1];
  if (anchor === undefined || last === undefined || ordered.length < 2) return null;
  return { serviceDays: ordered.slice(1), stayEndDate: last };
}

/**
 * Every morning this order is for, in the order they are read (D11).
 *
 * What the guest is shown must be the same set the wire is built from, so both
 * start here: `stayForWire` splits this into anchor plus repeat, and
 * `ChosenMorning` prints one row of it per line. They disagreed before - the
 * restated line named the ANCHOR alone, so a four-morning stay read as a
 * Wednesday everywhere except the checkout recap, and the guest's only view of
 * what they had booked contradicted what they were about to pay for.
 *
 * The anchor stands in for the set when there is no selection to read: a draft
 * written before the multi-select strip carries no `selectedDays`, and one
 * normalized past its last morning can be left empty. Either way the draft still
 * has a morning, and it is the one to show. Ascending, always, because these are
 * ISO calendar dates and a stay is read forwards.
 */
export function chosenMornings(
  selectedDays: readonly string[],
  anchor: string | null,
): readonly string[] {
  if (selectedDays.length > 0) return [...selectedDays].sort();
  return anchor === null ? [] : [anchor];
}
